import React, { useEffect, useRef, useState } from 'react';
import { usePageContext } from 'vike-react/usePageContext';
import { CaretDown, MagnifyingGlass, SquaresFour } from '@phosphor-icons/react';
import { Msg, useFormatMessage } from '../i18n-review/client/Msg';
import { BACKOFFICE_TOOL_CATEGORIES, BACKOFFICE_TOOL_ROUTES } from '../utils/backoffice/backoffice-tool-routes';
import { resolveBackofficeLocation } from '../utils/backoffice/backoffice-location';
import { useBackofficeToolPrefs } from '../hooks/useBackofficeToolPrefs';
import { BackofficeToolPicker, type BackofficePickerItem } from './backoffice/BackofficeToolPicker';

/**
 * Top bar shared by every backoffice page. Three ideas, the same at every width:
 *  1. "All tools" is always a labelled link to the hub (it used to be an unlabelled title).
 *  2. Where you are is always visible: the current tool, and from `lg` up its category siblings
 *     as one-click links (a row of all 17 tools never fit and was hidden behind an invisible
 *     scroll, so the row is now scoped to the current category, which always fits).
 *  3. Everything else is one search away: a picker (button, or Cmd/Ctrl+K) listing every tool,
 *     grouped and searchable. Below `lg` the current-tool chip is the picker's trigger.
 */
export function BackofficeNav() {
  const pageContext = usePageContext();
  // Use only pageContext (no window fallback) so server and client render the same isActive/className and avoid hydration mismatch
  const urlPathname =
    pageContext?.urlPathname ?? (pageContext as { urlParsed?: { pathname?: string } })?.urlParsed?.pathname ?? '';
  const formatMessage = useFormatMessage();
  const segments = urlPathname?.split('/').filter(Boolean) ?? [];
  const locale = (pageContext?.locale as string) || (['en', 'fi', 'sv'].includes(segments[0] ?? '') ? segments[0] : 'en');
  const prefix = ['en', 'fi', 'sv'].includes(locale) ? `/${locale}` : '';
  const hubHref = `${prefix}/backoffice`;
  const onBackofficeHub = urlPathname === hubHref;

  const [pickerOpen, setPickerOpen] = useState(false);
  const [shortcutHint, setShortcutHint] = useState('Ctrl K');
  const activeLinkRef = useRef<HTMLAnchorElement>(null);

  // Cmd/Ctrl+K opens (or closes) the picker from anywhere on the page.
  useEffect(() => {
    if (/Mac|iPhone|iPad/.test(navigator.platform)) setShortcutHint('⌘K');
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && !event.altKey && !event.shiftKey && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPickerOpen((open) => !open);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const { prefs, togglePin, recordVisit } = useBackofficeToolPrefs();
  // A sub-page (e.g. Monetisation plan) counts as its parent tool being current.
  const currentTool = resolveBackofficeLocation(urlPathname ?? '', prefix).tool;

  useEffect(() => {
    if (currentTool) recordVisit(currentTool.path);
  }, [currentTool?.path, recordVisit]);

  const navItems = BACKOFFICE_TOOL_ROUTES.map((item) => ({
    ...item,
    href: `${prefix}${item.path}`,
    isActive: item.path === currentTool?.path,
    label: formatMessage({ id: item.labelKey, defaultMessage: item.defaultLabel }),
  }));
  const activeItem = navItems.find((item) => item.isActive);
  const activeCategory = BACKOFFICE_TOOL_CATEGORIES.find((category) => category.id === activeItem?.category);
  const siblings = activeItem ? navItems.filter((item) => item.category === activeItem.category) : [];

  // Picker order follows the category order (BACKOFFICE_TOOL_ROUTES pins Outreach CRM first,
  // which would split its category in two groups here).
  const pickerItems: BackofficePickerItem[] = BACKOFFICE_TOOL_CATEGORIES.flatMap((category) =>
    navItems
      .filter((item) => item.category === category.id)
      .map((item) => ({
        path: item.path,
        href: item.href,
        label: item.label,
        description: formatMessage({ id: item.descriptionKey, defaultMessage: item.defaultDescription }),
        categoryId: category.id,
        categoryLabel: formatMessage({ id: category.labelKey, defaultMessage: category.defaultLabel }),
        icon: item.icon,
        isActive: item.isActive,
      })),
  );

  // Keep the current tool visible if the sibling row ever overflows at a narrow lg width.
  useEffect(() => {
    activeLinkRef.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [urlPathname]);

  const findToolLabel = formatMessage({ id: 'backoffice.nav.findTool', defaultMessage: 'Find a tool' });
  const ActiveIcon = activeItem?.icon;

  return (
    <>
      <nav
        aria-label={formatMessage({
          id: 'backoffice.title',
          defaultMessage: 'Backoffice',
          description: 'Title for the backoffice/admin navigation section',
        })}
        className="backoffice-glass border-0 rounded-none border-b border-black/5 dark:border-white/10 shadow-sm w-full"
      >
        <div className="w-full px-2 sm:px-3">
          <div className="flex items-center h-11 gap-1 min-w-0">
            <a
              href={hubHref}
              aria-current={onBackofficeHub ? 'page' : undefined}
              className={`inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg px-2 text-xs font-semibold transition-colors ${
                onBackofficeHub
                  ? 'bg-jc-accent-muted text-jc-accent'
                  : 'text-jc-ink hover:bg-black/6 dark:hover:bg-white/8'
              }`}
            >
              <SquaresFour className="h-4 w-4 shrink-0" aria-hidden />
              <Msg
                id="backoffice.hub.nav"
                defaultMessage="All tools"
                description="Navigation link to the backoffice hub listing all admin tools"
              />
            </a>
            <span className="mx-1 h-4 w-px shrink-0 bg-jc-border" aria-hidden="true" />

            {/* Below lg: the current tool doubles as the button that opens the picker. */}
            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              aria-haspopup="dialog"
              title={findToolLabel}
              className="flex h-9 min-w-0 flex-1 items-center justify-between gap-2 rounded-lg border border-jc-border bg-jc-surface/80 px-2.5 py-1.5 text-left text-xs font-medium text-jc-ink transition-colors hover:bg-jc-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-jc-accent/50 dark:bg-jc-elevated/80 lg:hidden"
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {ActiveIcon ? (
                  <ActiveIcon className="h-3.5 w-3.5 shrink-0 text-jc-accent" aria-hidden />
                ) : (
                  <MagnifyingGlass className="h-3.5 w-3.5 shrink-0 text-jc-muted" aria-hidden />
                )}
                <span className={`truncate ${activeItem ? '' : 'text-jc-muted'}`}>{activeItem?.label ?? findToolLabel}</span>
              </span>
              <CaretDown className="h-3.5 w-3.5 shrink-0 text-jc-muted" aria-hidden />
            </button>

            {/* lg and up: the current category's tools, one click each. */}
            <div className="hidden min-w-0 flex-1 items-center gap-0.5 overflow-x-auto scrollbar-none lg:flex">
              {activeCategory ? (
                <>
                  <span className="mr-1 hidden shrink-0 text-[11px] font-semibold uppercase tracking-wide text-jc-muted xl:inline">
                    {formatMessage({ id: activeCategory.labelKey, defaultMessage: activeCategory.defaultLabel })}
                  </span>
                  {siblings.map((item) => {
                    const Icon = item.icon;
                    return (
                      <a
                        key={item.href}
                        ref={item.isActive ? activeLinkRef : undefined}
                        href={item.href}
                        aria-current={item.isActive ? 'page' : undefined}
                        className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${
                          item.isActive
                            ? 'bg-jc-accent-muted text-jc-accent'
                            : 'text-jc-muted hover:bg-black/6 hover:text-jc-ink dark:hover:bg-white/8'
                        }`}
                      >
                        <Icon className="h-3.5 w-3.5 shrink-0" aria-hidden />
                        {item.label}
                      </a>
                    );
                  })}
                </>
              ) : null}
            </div>

            <button
              type="button"
              onClick={() => setPickerOpen(true)}
              aria-haspopup="dialog"
              className="ml-auto hidden h-8 shrink-0 items-center gap-2 rounded-lg border border-jc-border bg-jc-surface/80 px-2.5 py-1.5 text-xs font-medium text-jc-muted transition-colors hover:bg-jc-elevated hover:text-jc-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-jc-accent/50 dark:bg-jc-elevated/80 lg:inline-flex"
            >
              <MagnifyingGlass className="h-3.5 w-3.5" aria-hidden />
              {findToolLabel}
              <kbd className="hidden rounded border border-jc-border px-1.5 py-px font-sans text-[10px] font-semibold text-jc-muted xl:inline">
                {shortcutHint}
              </kbd>
            </button>
          </div>
        </div>
      </nav>
      <BackofficeToolPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        items={pickerItems}
        pinnedPaths={prefs.pinned}
        recentPaths={prefs.recent}
        onTogglePin={togglePin}
      />
    </>
  );
}
